import React, { useEffect, useState } from 'react';
import { X, FileText, Calendar, Edit3 } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { STANDARD_DOCUMENT_TYPES, fetchDocumentTypes, documentTypeOptions } from '../utils/documentTypes';
import './NewRequestForm.css';

const NewRequestForm = ({ onClose, onSuccess }) => {
  const { user } = useAuth();
  const [requestType, setRequestType] = useState('attestation');
  const [formData, setFormData] = useState({
    attestationType: 'work_certificate',
    purpose: '',
    additionalNotes: '',
    startDate: '',
    endDate: '',
    leaveType: 'vacation',
    reason: '',
    customTitle: '',
    customDescription: '',
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // The document types are managed by the admin (names, removed ones)
  const [documentTypes, setDocumentTypes] = useState(STANDARD_DOCUMENT_TYPES);
  const attestationTypes = documentTypeOptions(documentTypes);

  useEffect(() => {
    fetchDocumentTypes().then((types) => {
      setDocumentTypes(types);
      const offered = documentTypeOptions(types);
      // The pre-selected type may have been removed by the admin
      setFormData((prev) => (offered.some((o) => o.value === prev.attestationType) || offered.length === 0
        ? prev
        : { ...prev, attestationType: offered[0].value }));
    });
  }, []);

  const leaveTypes = [
    { value: 'vacation', label: 'Annual Leave (Vacation)' },
    { value: 'sick', label: 'Sick Leave' },
    { value: 'personal', label: 'Personal Leave' },
    { value: 'other', label: 'Other Leave' },
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      if (
        formData.startDate
        && formData.endDate
        && new Date(formData.endDate) < new Date(formData.startDate)
      ) {
        setError('End date cannot be before start date.');
        setSubmitting(false);
        return;
      }

      if (requestType === 'attestation') {
        await api.post('/attestations', {
          attestationType: formData.attestationType,
          purpose: formData.purpose,
          additionalNotes: formData.additionalNotes,
          startDate: formData.startDate || null,
          endDate: formData.endDate || null,
        });
      } else if (requestType === 'leave') {
        if (!user?.userId) {
          setError('User not authenticated. Please log in again.');
          setSubmitting(false);
          return;
        }
        await api.post('/absences', {
          userId: Number(user.userId),
          type: formData.leaveType,
          startDate: formData.startDate,
          endDate: formData.endDate,
          reason: formData.reason,
        });
      } else if (requestType === 'custom') {
        const fd = new FormData();
        fd.append('title', formData.customTitle);
        fd.append('description', formData.customDescription);
        if (formData.startDate) fd.append('startDate', formData.startDate);
        if (formData.endDate) fd.append('endDate', formData.endDate);
        if (file) fd.append('file', file);
        await api.upload('POST', '/requests', fd);
      }
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error submitting request:', err);
      setError(err.message || 'Error submitting request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>New Request</h2>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="request-type-tabs">
          <button
            className={`type-tab ${requestType === 'attestation' ? 'active' : ''}`}
            onClick={() => setRequestType('attestation')}
          >
            <FileText size={18} />
            Attestation
          </button>
          <button
            className={`type-tab ${requestType === 'leave' ? 'active' : ''}`}
            onClick={() => setRequestType('leave')}
          >
            <Calendar size={18} />
            Leave Request
          </button>
          <button
            className={`type-tab ${requestType === 'custom' ? 'active' : ''}`}
            onClick={() => setRequestType('custom')}
          >
            <Edit3 size={18} />
            Custom Request
          </button>
        </div>

        <form onSubmit={handleSubmit} className="request-form">
          {requestType === 'attestation' ? (
            <>
              <div className="form-group">
                <label>Attestation Type *</label>
                <select
                  name="attestationType"
                  value={formData.attestationType}
                  onChange={handleChange}
                  required
                >
                  {attestationTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Purpose *</label>
                <textarea
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleChange}
                  placeholder="Explain why you need this attestation..."
                  rows="3"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date (Optional)</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>End Date (Optional)</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    min={formData.startDate || undefined}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Additional Notes (Optional)</label>
                <textarea
                  name="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={handleChange}
                  placeholder="Any additional information..."
                  rows="2"
                />
              </div>
            </>
          ) : requestType === 'leave' ? (
            <>
              <div className="form-group">
                <label>Leave Type *</label>
                <select
                  name="leaveType"
                  value={formData.leaveType}
                  onChange={handleChange}
                  required
                >
                  {leaveTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    min={formData.startDate || undefined}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Reason *</label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  placeholder="Explain the reason for your leave..."
                  rows="3"
                  required
                />
              </div>
            </>
          ) : (
            <>
              <div className="form-group">
                <label>Request Title *</label>
                <input
                  type="text"
                  name="customTitle"
                  value={formData.customTitle}
                  onChange={handleChange}
                  placeholder="Enter a title for your request..."
                  required
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <textarea
                  name="customDescription"
                  value={formData.customDescription}
                  onChange={handleChange}
                  placeholder="Describe your request in detail..."
                  rows="4"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date (Optional)</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>End Date (Optional)</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    min={formData.startDate || undefined}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Attach File (Optional)</label>
                <input
                  type="file"
                  onChange={(e) => setFile(e.target.files[0] || null)}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls,.csv"
                />
                {file && <p className="file-name">Selected: {file.name}</p>}
              </div>
            </>
          )}

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewRequestForm;
